import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, tokenManager } from '@/core/lib/api';

// =============================================================================
// Types
// =============================================================================

export type UserRole = 'family' | 'attorney' | 'vendor' | 'provider' | 'admin';

export interface User {
  id: string;
  email: string;
  email_verified: boolean;
  first_name: string;
  last_name: string;
  role: UserRole;
  status: 'pending' | 'active' | 'suspended' | 'deleted';
  avatar_url?: string;
  phone?: string;
  timezone?: string;
}

interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
  
  // Computed helpers
  hasRole: (role: UserRole | UserRole[]) => boolean;
  isEmailVerified: () => boolean;
}

// =============================================================================
// Store
// =============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Login
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await api.auth.login({ email, password });
          tokenManager.set(data.access_token);
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          const message =
            error.response?.data?.detail ||
            error.message ||
            'Login failed. Please try again.';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      // Register
      register: async (registerData: RegisterData) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await api.auth.register(registerData);
          tokenManager.set(data.access_token);
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          const message =
            error.response?.data?.detail ||
            error.message ||
            'Registration failed. Please try again.';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      // Logout
      logout: async () => {
        try {
          await api.auth.logout();
        } catch {
          // Ignore errors - just clear local state
        }
        tokenManager.remove();
        set({ user: null, isAuthenticated: false, error: null });
      },

      // Fetch current user
      fetchUser: async () => {
        // Check if we have a valid token first
        if (!tokenManager.isValid()) {
          tokenManager.remove();
          set({ user: null, isAuthenticated: false, isLoading: false });
          return;
        }

        set({ isLoading: true });
        try {
          const { data } = await api.auth.me();
          set({
            user: data,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch {
          tokenManager.remove();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      // Set user directly (useful for SSR or external updates)
      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user });
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Check if user has a specific role (or one of multiple roles)
      hasRole: (role: UserRole | UserRole[]) => {
        const { user } = get();
        if (!user) return false;
        const roles = Array.isArray(role) ? role : [role];
        return roles.includes(user.role);
      },

      // Check if email is verified
      isEmailVerified: () => {
        const { user } = get();
        return user?.email_verified ?? false;
      },
    }),
    {
      name: 'brightpath-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;
